
import React, { useState, useEffect, useRef } from 'react';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Stack,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DoneIcon from '@mui/icons-material/Done';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import NumbersIcon from "@mui/icons-material/Numbers";
import EventIcon from "@mui/icons-material/Event";
import SchoolIcon from "@mui/icons-material/School";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LinkIcon from "@mui/icons-material/Link";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Custom Components
import TopNav from './Navbar';
import LeftNavbar from './LeftNav';
import Breadcrumb from './BreadCrump'; // Assuming it's in same folder



const getStatusChip = (status) => {
  switch (status) {
    case 'SUCCESS': return <Chip icon={<DoneIcon />} label="Success" color="success" size="small" />;
    case 'PENDING': return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
    case 'FAILURE': return <Chip icon={<CancelIcon />} label="Failed" color="error" size="small" />;
    default: return <Chip label={status || 'Unknown'} size="small" />;
  }
};


const Payments = () => {
  const token = localStorage.getItem('token');
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);

  // Derived summary values
  const totalPaid = transactions
    .filter(txn => txn.status === "SUCCESS")
    .reduce((sum, txn) => sum + (txn.amount_k || 0), 0);

  const totalPending = transactions
    .filter(txn => txn.status === "PENDING")
    .reduce((sum, txn) => sum + (txn.amount_k || 0), 0);

  const totalFailed = transactions
    .filter(txn => txn.status === "FAILURE")
    .reduce((sum, txn) => sum + (txn.amount_k || 0), 0);

  // Fetch user
  useEffect(() => {
    if (!token) return;
    fetch('http://196.216.9.34:5020/api/users/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(setUser);
  }, [token]);

  // Fetch transactions
  useEffect(() => {
    if (!user) return;
    fetch(`http://196.216.9.34:5050/api/transactions/${user.id}`)
      .then(res => res.json())
      .then(setTransactions)
      .catch(console.error);
  }, [user]);

  return (
    <Box sx={{ display: 'flex' }}>
      <LeftNavbar />
      <Box sx={{ flexGrow: 1, ml: { lg: '240px' } }}>
        <TopNav />
        <Box sx={{ p: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
          <Breadcrumb currentPage="Payments" />

          <Typography variant="h4" fontWeight="bold" sx={{ mb: 4, color: '#ff6600' }}>
            <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Payments
          </Typography>

          {/* Payment Summary */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderLeft: '6px solid #ff6600', boxShadow: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#ff6600' }}>
                      <ReceiptLongIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">Total Paid</Typography>
                      <Typography variant="h6">k{totalPaid}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderLeft: '6px solid #ff6600', boxShadow: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#ff6600' }}>
                      <PendingIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">Pending</Typography>
                      <Typography variant="h6">k{totalPending}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderLeft: '6px solid #ff6600', boxShadow: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#ff6600' }}>
                      <CancelIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">Failed</Typography>
                      <Typography variant="h6">k{totalFailed}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Transactions Table */}
          <Card>
              <CardContent>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell>
                        <NumbersIcon sx={{ color: "#1976d2", mr: 1, verticalAlign: "middle" }} />
                        <strong>ID</strong>
                      </TableCell>
                      <TableCell>
                        <EventIcon sx={{ color: "#0288d1", mr: 1, verticalAlign: "middle" }} />
                        <strong>Date</strong>
                      </TableCell>
                      <TableCell>
                        <SchoolIcon sx={{ color: "#7b1fa2", mr: 1, verticalAlign: "middle" }} />
                        <strong>Course</strong>
                      </TableCell>
                      <TableCell>
                        <ReceiptIcon sx={{ color: "#ef6c00", mr: 1, verticalAlign: "middle" }} />
                        <strong>Order ID</strong>
                      </TableCell>
                      <TableCell>
                        <LinkIcon sx={{ color: "#2e7d32", mr: 1, verticalAlign: "middle" }} />
                        <strong>Form URL</strong>
                      </TableCell>
                      <TableCell>
                        <PaymentIcon sx={{ color: "#6a1b9a", mr: 1, verticalAlign: "middle" }} />
                        <strong>Method</strong>
                      </TableCell>
                      <TableCell>
                        <MonetizationOnIcon sx={{ color: "#d32f2f", mr: 1, verticalAlign: "middle" }} />
                        <strong>Amount (k)</strong>
                      </TableCell>
                      <TableCell>
                        <CheckCircleIcon sx={{ color: "#388e3c", mr: 1, verticalAlign: "middle" }} />
                        <strong>Status</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map(txn => (
                      <TableRow key={txn.id}>
                        <TableCell>{txn.id}</TableCell>
                        <TableCell>{txn.txn_date}</TableCell>
                        <TableCell>{txn.course_id}</TableCell>
                        <TableCell>{txn.order_id}</TableCell>
                        <TableCell>{txn.form_url ? <a href={txn.form_url} target="_blank" rel="noopener noreferrer">View</a> : '-'}</TableCell>
                        <TableCell>{txn.method}</TableCell>
                        <TableCell>{txn.amount_k}</TableCell>
                        <TableCell>{getStatusChip(txn.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

        </Box>
      </Box>
    </Box>
  );
};

export default Payments;
